import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Leaf, Flame, Wifi, Car, Sun, Droplet, 
  Home, Bath, Bed, Users, Coffee, Laptop 
} from "lucide-react";
import { Button } from "@/components/ui/button";

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/avi6rx8r_WhatsApp%20Image%202026-02-28%20at%2014.45.44%20%283%29.jpeg",
  bedroom1: "https://customer-assets.emergentagent.com/job_9aafd9a9-bdf8-4d35-9ad3-a45b89c1eed6/artifacts/athrsayn_image.png",
  bedroom2: "https://customer-assets.emergentagent.com/job_9aafd9a9-bdf8-4d35-9ad3-a45b89c1eed6/artifacts/j7pdp7ks_image.png",
  kitchen: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/g7m2peu1_WhatsApp%20Image%202026-02-28%20at%2014.15.30%20%282%29.jpeg",
  living: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/7hts6rhl_WhatsApp%20Image%202026-02-28%20at%2014.45.44.jpeg",
  deck: "https://customer-assets.emergentagent.com/job_9aafd9a9-bdf8-4d35-9ad3-a45b89c1eed6/artifacts/bgi62tnf_image.png",
  outdoor: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/ho3mic30_WhatsApp%20Image%202026-02-28%20at%2014.15.30.jpeg",
};

const amenities = [
  { icon: Leaf, name: "Garden & Bush Views", desc: "Surrounded by indigenous vegetation" },
  { icon: Flame, name: "Fire Pit", desc: "Cozy evenings by the fire" },
  { icon: Coffee, name: "Fully Equipped Kitchen", desc: "Everything you need to cook" },
  { icon: Wifi, name: "High-Speed WiFi", desc: "Stay connected" },
  { icon: Laptop, name: "Dedicated Workspace", desc: "Perfect for remote work" },
  { icon: Car, name: "Free Parking", desc: "Secure on-site parking" },
  { icon: Sun, name: "Solar Power", desc: "Off-grid sustainable energy" },
  { icon: Droplet, name: "RO Drinking Water", desc: "Pure borehole water" },
  { icon: Leaf, name: "Birdwatching", desc: "Abundant local birdlife" },
];

const HeroSection = () => (
  <section className="relative h-[70vh] min-h-[500px]" data-testid="space-hero">
    <img
      src={IMAGES.hero}
      alt="Weaver's Nest exterior"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
    <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
      <div className="container mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <p className="text-sm uppercase tracking-[0.3em] text-white/80 mb-4">
            Discover
          </p>
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl text-white mb-4">
            The Space
          </h1>
          <p className="text-lg text-white/90 max-w-2xl">
            Eco-conscious luxury meets natural beauty in this thoughtfully designed retreat.
          </p>
        </motion.div>
      </div>
    </div>
  </section>
);

const OverviewSection = () => (
  <section className="py-20 md:py-32 bg-white" data-testid="space-overview">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <p className="section-subheading">About the Property</p>
          <h2 className="section-heading mb-6">A Perfect Blend of Comfort & Nature</h2>
          <div className="prose prose-lg text-muted-foreground">
            <p className="leading-relaxed mb-6">
              Built on the foundation of an old labourer's cottage, with eco consciousness 
              and sustainability as a core focus, we have created a cozy space that invites 
              the feel of the outside in.
            </p>
            <p className="leading-relaxed mb-6">
              The two bedrooms and two bathrooms are all tastefully decorated in natural 
              tones and textures with accents of forest green. The lounge and kitchen are 
              light, open spaces with rustic floors and live edge wood shelves.
            </p>
            <p className="leading-relaxed">
              Stack-away doors open up to the covered deck, leading onto an outside 
              entertainment area which boasts a cozy fire pit, seating and dining 
              facilities, plus the wood-fired hot tub for ultimate relaxation.
            </p>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 gap-4"
        >
          {[
            { icon: Users, value: "4", label: "Guests" },
            { icon: Bed, value: "2", label: "Bedrooms" },
            { icon: Bath, value: "2", label: "Bathrooms" },
            { icon: Home, value: "1.8ha", label: "Property" },
          ].map((stat, index) => (
            <div 
              key={index}
              className="bg-brand-cream p-6 rounded-2xl text-center"
            >
              <stat.icon className="w-8 h-8 text-brand-sage mx-auto mb-3" />
              <p className="font-heading text-3xl text-brand-charcoal">{stat.value}</p>
              <p className="text-sm text-muted-foreground">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  </section>
);

const BedroomsSection = () => (
  <section className="py-20 md:py-32 bg-brand-cream" data-testid="space-bedrooms">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <p className="section-subheading">Rest & Recharge</p>
        <h2 className="section-heading">Where You'll Sleep</h2>
      </motion.div>
      
      <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
        {/* Main Bedroom */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-3xl overflow-hidden border border-brand-border"
        >
          <div className="img-zoom aspect-[4/3]">
            <img
              src={IMAGES.bedroom1}
              alt="Main Bedroom"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-8">
            <h3 className="font-heading text-2xl mb-3">Main Bedroom</h3>
            <p className="text-muted-foreground leading-relaxed">
              The main bedroom features an en-suite bathroom and sliding doors that 
              open to the wraparound deck with beautiful bush and tree views. 
              Quality mattress and bedding ensure a restful night's sleep.
            </p>
            <div className="flex gap-3 mt-4">
              <span className="text-sm bg-brand-muted px-3 py-1 rounded-full">Queen Bed</span>
              <span className="text-sm bg-brand-muted px-3 py-1 rounded-full">En-suite</span>
              <span className="text-sm bg-brand-muted px-3 py-1 rounded-full">Deck Access</span>
            </div>
          </div>
        </motion.div>
        
        {/* Treehouse Suite */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl overflow-hidden border border-brand-border"
        >
          <div className="img-zoom aspect-[4/3]">
            <img
              src={IMAGES.bedroom2}
              alt="Treehouse Suite"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-8">
            <h3 className="font-heading text-2xl mb-3">The Treehouse Suite</h3>
            <p className="text-muted-foreground leading-relaxed">
              A loft-style bedroom accessed by a ladder-type staircase. This room 
              boasts the best view in the nest, with large windows overlooking the 
              surrounding bush and valley. A birdwatcher's hideaway with a dedicated workspace.
            </p>
            <div className="flex gap-3 mt-4">
              <span className="text-sm bg-brand-muted px-3 py-1 rounded-full">Queen Bed</span>
              <span className="text-sm bg-brand-muted px-3 py-1 rounded-full">Valley Views</span>
              <span className="text-sm bg-brand-muted px-3 py-1 rounded-full">Workspace</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

const LivingSpacesSection = () => (
  <section className="py-20 md:py-32 bg-white" data-testid="space-living">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="order-2 lg:order-1"
        >
          <p className="section-subheading">Living Spaces</p>
          <h2 className="section-heading mb-6">Light, Open & Inviting</h2>
          <p className="text-muted-foreground text-lg leading-relaxed mb-6">
            The lounge and kitchen are light, open spaces with rustic floors, 
            live edge wood shelves, and a statement kitchen island. The kitchen 
            is fully equipped for all your culinary needs.
          </p>
          <p className="text-muted-foreground text-lg leading-relaxed mb-8">
            Stack-away doors blur the line between indoors and out, opening onto 
            the covered deck and entertainment area. It's the perfect space for 
            gathering with loved ones or finding a quiet corner to read.
          </p>
          <div className="flex flex-wrap gap-3">
            <span className="text-sm bg-brand-muted px-4 py-2 rounded-full">Full Kitchen</span>
            <span className="text-sm bg-brand-muted px-4 py-2 rounded-full">Open Plan Living</span>
            <span className="text-sm bg-brand-muted px-4 py-2 rounded-full">Indoor-Outdoor Flow</span>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="order-1 lg:order-2 grid grid-cols-2 gap-4"
        >
          <div className="img-zoom rounded-2xl overflow-hidden">
            <img
              src={IMAGES.kitchen}
              alt="Kitchen"
              className="w-full aspect-square object-cover"
            />
          </div>
          <div className="img-zoom rounded-2xl overflow-hidden mt-8">
            <img
              src={IMAGES.living}
              alt="Living Area"
              className="w-full aspect-square object-cover"
            />
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

const OutdoorSection = () => (
  <section className="py-20 md:py-32 bg-brand-charcoal text-white" data-testid="space-outdoor">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 gap-4"
        >
          <div className="img-zoom rounded-2xl overflow-hidden">
            <img
              src={IMAGES.deck}
              alt="Deck"
              className="w-full aspect-[3/4] object-cover"
            />
          </div>
          <div className="img-zoom rounded-2xl overflow-hidden mt-8">
            <img
              src={IMAGES.outdoor}
              alt="Outdoor Area"
              className="w-full aspect-[3/4] object-cover"
            />
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-sm uppercase tracking-[0.3em] text-brand-sage mb-4">
            Outdoor Living
          </p>
          <h2 className="font-heading text-4xl md:text-5xl mb-6">
            Your Private Sanctuary
          </h2>
          <p className="text-gray-300 text-lg leading-relaxed mb-6">
            The covered deck extends your living space outdoors, while the fire pit 
            area provides the perfect setting for evening gatherings. Relax and unwind 
            surrounded by nature under the stars.
          </p>
          <p className="text-gray-300 text-lg leading-relaxed mb-8">
            Explore the 1.8 hectare property, surrounded by indigenous bush and 
            abundant birdlife. It's a nature lover's paradise.
          </p>
          <div className="flex flex-wrap gap-3">
            <span className="text-sm border border-white/30 px-4 py-2 rounded-full">Fire Pit</span>
            <span className="text-sm border border-white/30 px-4 py-2 rounded-full">Covered Deck</span>
            <span className="text-sm border border-white/30 px-4 py-2 rounded-full">Bush Views</span>
            <span className="text-sm border border-white/30 px-4 py-2 rounded-full">Birdwatching</span>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

const AmenitiesSection = () => (
  <section className="py-20 md:py-32 bg-white" data-testid="space-amenities">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <p className="section-subheading">Amenities</p>
        <h2 className="section-heading">What This Place Offers</h2>
      </motion.div>
      
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {amenities.map((amenity, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.05 }}
            className="bg-brand-cream p-6 rounded-xl text-center"
          >
            <amenity.icon className="w-8 h-8 text-brand-sage mx-auto mb-3" />
            <h3 className="font-medium text-brand-charcoal mb-1">{amenity.name}</h3>
            <p className="text-sm text-muted-foreground">{amenity.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const CTASection = () => (
  <section className="py-20 md:py-32 bg-brand-cream" data-testid="space-cta">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h2 className="section-heading mb-6">Ready to Experience Weaver's Nest?</h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-10">
          Book directly via WhatsApp and save on platform fees. Your nature escape awaits.
        </p>
        <a 
          href="https://api.whatsapp.com/send?phone=%2B27832329940&text=Hi%2C%20I%20would%20like%20to%20book%20a%20stay%20at%20Weaver%27s%20Nest%20Cottage" 
          target="_blank"
          rel="noopener noreferrer"
          data-testid="space-book-btn"
        >
          <Button className="btn-primary text-lg px-10 py-6">
            Book via WhatsApp
          </Button>
        </a>
      </motion.div>
    </div>
  </section>
);

export default function TheSpacePage() {
  return (
    <div data-testid="space-page">
      <HeroSection />
      <OverviewSection />
      <BedroomsSection />
      <LivingSpacesSection />
      <OutdoorSection />
      <AmenitiesSection />
      <CTASection />
    </div>
  );
}
